import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";
import SeoHead from "../components/SeoHead";
import { useSettings } from "../contexts/SettingsContext";
import { useI18n } from "../contexts/I18nContext";

/**
 * Generic legal page renderer. `kind` = "terms" or "privacy".
 */
export default function LegalPage({ kind = "terms" }) {
  const { settings } = useSettings();
  const { lang } = useI18n();
  const [html, setHtml] = useState("");

  useEffect(() => {
    const legal = settings?.legal || {};
    const key = `${kind}_${lang}`;
    const fallbackKey = `${kind}_ro`;
    const raw = (legal[key] || legal[fallbackKey] || "").trim();
    setHtml(raw ? DOMPurify.sanitize(raw, {
      ALLOWED_TAGS: ["p","strong","em","u","s","a","ul","ol","li","h1","h2","h3","h4","h5","h6","blockquote","br","hr","img","pre","code","span","div","table","thead","tbody","tr","td","th"],
      ALLOWED_ATTR: ["href","title","target","rel","src","alt","style","class"],
    }) : "");
  }, [settings, lang, kind]);

  const titles = {
    terms: { ro: "Termeni si conditii", en: "Terms and Conditions" },
    privacy: { ro: "Politica de confidentialitate", en: "Privacy Policy" },
  };
  const title = titles[kind]?.[lang] || titles[kind]?.ro || "";

  const updatedAt = settings?.legal?.[`${kind}_updated_at`] || "";

  return (
    <div>
      <SeoHead title={title} description={title} />
      <PublicHeader />
      <section className="pt-32 pb-20 px-6 md:px-12 max-w-3xl mx-auto min-h-screen" data-testid={`legal-${kind}`}>
        <div className="label-eyebrow mb-3" style={{ color: "var(--brand-primary)" }}>
          {lang === "en" ? "Legal" : "Informatii legale"}
        </div>
        <h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mb-2">{title}</h1>
        {updatedAt && (
          <p className="text-xs mb-10" style={{ color: "var(--brand-text-secondary)" }}>
            {lang === "en" ? "Last updated" : "Ultima actualizare"}: {updatedAt}
          </p>
        )}

        {html ? (
          <div
            className="legal-content prose-invert text-sm leading-relaxed"
            style={{ color: "var(--brand-text-secondary)" }}
            data-testid={`legal-${kind}-content`}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <div className="card-surface p-8 text-center" data-testid={`legal-${kind}-empty`}>
            <p className="text-sm" style={{ color: "var(--brand-text-secondary)" }}>
              {lang === "en"
                ? "This content is being prepared. Please check back soon."
                : "Acest continut este in pregatire. Reveniti curand."}
            </p>
          </div>
        )}
      </section>
      <PublicFooter />
      <style>{`
        .legal-content h1, .legal-content h2, .legal-content h3 { color: var(--brand-text-primary); font-family: Georgia, serif; font-weight: 300; margin-top: 2rem; margin-bottom: 1rem; }
        .legal-content h1 { font-size: 1.8rem; }
        .legal-content h2 { font-size: 1.4rem; }
        .legal-content h3 { font-size: 1.15rem; }
        .legal-content p { margin: 0.75rem 0; }
        .legal-content ul, .legal-content ol { margin: 0.75rem 0 0.75rem 1.5rem; }
        .legal-content li { margin: 0.3rem 0; }
        .legal-content a { color: var(--brand-primary); text-decoration: underline; }
        .legal-content strong { color: var(--brand-text-primary); }
        .legal-content table { width: 100%; border-collapse: collapse; margin: 1rem 0; }
        .legal-content td, .legal-content th { border: 1px solid var(--brand-border); padding: 0.5rem; }
      `}</style>
    </div>
  );
}
